// Utilities for locating blocks by id inside a Song draft.
// Every edit needs to find its target inside the immer draft before
// mutating; these walk the tree once and return a reference (or
// undefined). Used inside immer producer recipes.

import { isConditionalEffect } from './types';
import type {
  ArrangeEntry,
  Effect,
  Expression,
  Song,
  Statement,
  VoiceBlock,
} from './types';

// ─── Removal ────────────────────────────────────────────────────────

/** Remove the block with the given id from anywhere in the tree.
 * Returns true if something was removed. */
export function removeById(song: Song, id: string): boolean {
  const stmtIdx = song.statements.findIndex((s) => s.id === id);
  if (stmtIdx >= 0) {
    song.statements.splice(stmtIdx, 1);
    return true;
  }
  for (const stmt of song.statements) {
    if (stmt.kind === 'const' || stmt.kind === 'expression') {
      if (removeFromExpression(stmt.expr, id)) return true;
    }
  }
  return false;
}

function removeFromExpression(expr: Expression, id: string): boolean {
  if (expr.kind === 'stack') {
    const idx = expr.children.findIndex((c) => c.id === id);
    if (idx >= 0) {
      expr.children.splice(idx, 1);
      return true;
    }
    for (const c of expr.children) {
      if (removeFromExpression(c, id)) return true;
    }
  } else if (expr.kind === 'arrange') {
    const idx = expr.entries.findIndex((e) => e.id === id);
    if (idx >= 0) {
      expr.entries.splice(idx, 1);
      return true;
    }
    for (const e of expr.entries) {
      if (removeFromExpression(e.section, id)) return true;
    }
  }
  if (expr.kind === 'voice.s' || expr.kind === 'voice.note') {
    if (removeFromEffects(expr.effects, id)) return true;
  }
  if (expr.kind === 'ref' && expr.effects) {
    if (removeFromEffects(expr.effects, id)) return true;
  }
  return false;
}

// Remove an effect from any level of an effect chain, including nested
// conditional bodies. Returns true on a hit.
function removeFromEffects(effects: Effect[], id: string): boolean {
  const idx = effects.findIndex((e) => e.id === id);
  if (idx >= 0) {
    effects.splice(idx, 1);
    return true;
  }
  for (const e of effects) {
    if (isConditionalEffect(e) && removeFromEffects(e.body, id)) return true;
  }
  return false;
}

export function findStatement(song: Song, id: string): Statement | undefined {
  return song.statements.find((s) => s.id === id);
}

// Rename every ref block that points at `oldName` to use `newName`.
// Called after a const rename so references update in lock-step.
// Also retargets arrange entries and stack children recursively.
export function renameRefs(song: Song, oldName: string, newName: string): void {
  for (const stmt of song.statements) {
    if (stmt.kind === 'const' || stmt.kind === 'expression') {
      renameInExpression(stmt.expr, oldName, newName);
    }
  }
}

function renameInExpression(
  expr: Expression,
  oldName: string,
  newName: string,
): void {
  if (expr.kind === 'ref') {
    if (expr.name === oldName) expr.name = newName;
    return;
  }
  if (expr.kind === 'stack') {
    for (const c of expr.children) renameInExpression(c, oldName, newName);
    return;
  }
  if (expr.kind === 'arrange') {
    for (const e of expr.entries) renameInExpression(e.section, oldName, newName);
    return;
  }
  // Voices and custom functions don't hold nested refs; nothing to do.
}

export function findExpression(
  song: Song,
  id: string,
): Expression | undefined {
  for (const stmt of song.statements) {
    if (stmt.kind === 'const' || stmt.kind === 'expression') {
      const found = walkExpression(stmt.expr, id);
      if (found) return found;
    }
  }
  return undefined;
}

export function findEffect(song: Song, id: string): Effect | undefined {
  for (const stmt of song.statements) {
    if (stmt.kind === 'const' || stmt.kind === 'expression') {
      const found = walkEffectInExpression(stmt.expr, id);
      if (found) return found;
    }
  }
  return undefined;
}

export function findArrangeEntry(
  song: Song,
  id: string,
): ArrangeEntry | undefined {
  for (const stmt of song.statements) {
    if (stmt.kind === 'const' || stmt.kind === 'expression') {
      const found = walkArrangeEntryInExpression(stmt.expr, id);
      if (found) return found;
    }
  }
  return undefined;
}

function walkExpression(expr: Expression, id: string): Expression | undefined {
  if (expr.id === id) return expr;
  if (expr.kind === 'stack') {
    for (const child of expr.children) {
      const found = walkExpression(child, id);
      if (found) return found;
    }
  } else if (expr.kind === 'arrange') {
    for (const entry of expr.entries) {
      const found = walkExpression(entry.section, id);
      if (found) return found;
    }
  }
  return undefined;
}

function walkEffectInExpression(
  expr: Expression,
  id: string,
): Effect | undefined {
  if (hasEffects(expr)) {
    const found = walkEffectList(expr.effects ?? [], id);
    if (found) return found;
  }
  if (expr.kind === 'stack') {
    for (const child of expr.children) {
      const found = walkEffectInExpression(child, id);
      if (found) return found;
    }
  } else if (expr.kind === 'arrange') {
    for (const entry of expr.entries) {
      const found = walkEffectInExpression(entry.section, id);
      if (found) return found;
    }
  }
  return undefined;
}

function walkEffectList(effects: Effect[], id: string): Effect | undefined {
  for (const e of effects) {
    if (e.id === id) return e;
    if (isConditionalEffect(e)) {
      const nested = walkEffectList(e.body, id);
      if (nested) return nested;
    }
  }
  return undefined;
}

function walkArrangeEntryInExpression(
  expr: Expression,
  id: string,
): ArrangeEntry | undefined {
  if (expr.kind === 'arrange') {
    for (const entry of expr.entries) {
      if (entry.id === id) return entry;
      const nested = walkArrangeEntryInExpression(entry.section, id);
      if (nested) return nested;
    }
  } else if (expr.kind === 'stack') {
    for (const child of expr.children) {
      const found = walkArrangeEntryInExpression(child, id);
      if (found) return found;
    }
  }
  return undefined;
}

function hasEffects(
  expr: Expression,
): expr is VoiceBlock | (Expression & { kind: 'ref' }) {
  return expr.kind === 'voice.s' || expr.kind === 'voice.note' || expr.kind === 'ref';
}
